import React, { useContext, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { EditorState, convertToRaw, convertFromRaw } from 'draft-js'
import { Editor } from 'react-draft-wysiwyg'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import {
  Avatar,
  Content,
  Layout,
  Navigation,
  Responsive,
  Button,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Textarea,
  Checkbox,
  Indicators,
} from 'ui'
import {
  useResourceDocumentBySlug,
  useCreateResourceDocumentMutation,
  useUpdateResourceDocumentMutation,
  useDeleteResourceDocumentMutation,
  useResourceCategories,
  ResourceDocumentStatus,
} from '../../../queries'
import { useQuery } from '../../../routes'
import { toast } from 'react-hot-toast'

// Validation schema for resource editing
const resourceSchema = yup.object({
  title: yup.string().required('Title is required'),
  excerpt: yup.string().optional(),
  content: yup.string().optional(),
  status: yup.string().oneOf(['DRAFT', 'PUBLISHED']).required(),
  featured: yup.boolean().optional(),
  category_id: yup.string().optional(),
  color: yup.string().optional(),
})

export type ResourceFormData = yup.InferType<typeof resourceSchema>

export const ResourceDetailView: React.FC = () => {
  const { useDisplayMode } = useContext(Responsive.DrawerManagerContext)
  useDisplayMode('expanded')

  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const isEditMode = location.pathname.includes('/edit/')
  const isNewMode = location.pathname.includes('/new')

  // Only fetch resource when not in new mode (empty string disables the query)
  const { data, isLoading: loading, error } = useResourceDocumentBySlug(
    isNewMode ? '' : slug!
  )
  const resource = data?.document

  // Resource categories for dropdown
  const { data: categoriesData } = useResourceCategories()
  const categories = categoriesData || []

  // Create mutation
  const [
    createResourceMutation,
    { loading: createLoading },
  ] = useCreateResourceDocumentMutation()

  // Update mutation
  const [
    updateResourceMutation,
    { loading: updateLoading },
  ] = useUpdateResourceDocumentMutation()

  // Delete mutation
  const [
    deleteResourceDocument,
    { loading: deleteLoading },
  ] = useDeleteResourceDocumentMutation()
  const { set, search } = useQuery<{ modal: string }>()
  const [modalError, setModalError] = useState<string | null>(null)

  const handleDestroy = async () => {
    try {
      const result = await deleteResourceDocument({
        variables: { documentId: resource!.id },
      })
      if (result.success) {
        toast.success('Resource deleted successfully')
        navigate('/admin/resources')
      } else {
        const errors = result.errors || []
        setModalError(
          errors[0]?.message ?? 'There was a problem deleting this resource.'
        )
      }
    } catch (error) {
      setModalError('There was a problem deleting this resource.')
    }
  }

  // Form setup
  const form = useForm<ResourceFormData>({
    resolver: yupResolver(resourceSchema),
    defaultValues: {
      title: '',
      excerpt: '',
      content: '',
      status: ResourceDocumentStatus.DRAFT,
      featured: false,
      category_id: '',
      color: '#3b82f6',
    },
  })

  // Rich text editor state for content
  const [editorState, setEditorState] = useState(EditorState.createEmpty())

  // Update form when resource data loads
  useEffect(() => {
    if (resource && isEditMode) {
      form.reset({
        title: resource.title || '',
        excerpt: resource.excerpt || '',
        content: resource.content || '',
        status: resource.status || ResourceDocumentStatus.DRAFT,
        featured: resource.featured || false,
        category_id: resource.category_id || '',
        color: resource.color || '#3b82f6',
      })

      // Initialize editor state with existing content
      if (resource.content) {
        try {
          const contentState = convertFromRaw(JSON.parse(resource.content))
          setEditorState(EditorState.createWithContent(contentState))
        } catch (error) {
          console.warn(
            'Failed to parse existing content as Draft.js format, using plain text'
          )
          setEditorState(EditorState.createEmpty())
        }
      } else {
        setEditorState(EditorState.createEmpty())
      }
    }
  }, [resource, isEditMode, form])

  // Handle rich text editor changes
  const onEditorStateChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState)

    // Convert editor state to raw format and update form
    const contentState = newEditorState.getCurrentContent()
    const rawContent = convertToRaw(contentState)
    const contentString = JSON.stringify(rawContent)

    form.setValue('content', contentString, { shouldValidate: true })
  }

  // Handle form submission for both create and edit
  const handleSubmit = async (values: ResourceFormData) => {
    // Get the latest content from editor state
    const contentState = editorState.getCurrentContent()
    const rawContent = convertToRaw(contentState)
    const contentString = JSON.stringify(rawContent)

    if (isNewMode) {
      // Create new resource
      try {
        const result = await createResourceMutation({
          variables: {
            payload: {
              title: values.title,
              excerpt: values.excerpt,
              content: contentString,
              status: values.status as ResourceDocumentStatus,
              featured: values.featured,
              category_id: values.category_id || undefined,
              color: values.color,
            },
          },
        })

        toast.success('Resource created successfully')
        // Navigate to the newly created resource
        const newSlug = result?.document?.slug
        if (newSlug) {
          navigate(`/admin/resources/show/${newSlug}`)
        } else {
          navigate('/admin/resources')
        }
      } catch (error) {
        console.error('Error creating resource:', error)
        toast.error('Failed to create resource')
      }
    } else {
      // Update existing resource
      if (!resource?.id) {
        toast.error('Resource ID not found')
        return
      }

      try {
        await updateResourceMutation({
          variables: {
            documentId: resource.id,
            payload: {
              title: values.title,
              excerpt: values.excerpt,
              content: contentString,
              status: values.status as ResourceDocumentStatus,
              featured: values.featured,
              category_id: values.category_id || undefined,
              color: values.color,
            },
          },
        })

        toast.success('Resource updated successfully')
        navigate(`/admin/resources/show/${slug}`)
      } catch (error) {
        console.error('Error updating resource:', error)
        toast.error('Failed to update resource')
      }
    }
  }

  // Always render something to debug
  console.log(
    'ResourceDetailView: About to render, loading:',
    loading,
    'isNewMode:',
    isNewMode
  )

  // Show loading only when fetching existing resource (not in new mode)
  if (loading && !isNewMode) {
    return (
      <Layout.PaddedContent className="pb-24 md:pb-32">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading resource...</div>
        </div>
      </Layout.PaddedContent>
    )
  }

  // Show error only when fetching existing resource failed (not in new mode)
  if ((error || !resource) && !isNewMode) {
    return (
      <Layout.PaddedContent className="pb-24 md:pb-32">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Resource Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The resource you're looking for could not be found.
          </p>
          <Link
            to="/admin/resources"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Return to Resources
          </Link>
        </div>
      </Layout.PaddedContent>
    )
  }

  // Render create form when in new mode
  if (isNewMode) {
    return (
      <Layout.PaddedContent className="pb-24 md:pb-32">
        <Helmet>
          <title>Create New Resource - MCSS Pro Connect</title>
        </Helmet>

        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <Navigation.ReturnLink to="/admin/resources">
              Back to Resources
            </Navigation.ReturnLink>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-8">
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Create New Resource
                </h1>
                <p className="text-gray-600">
                  Fill in the details below to create a new resource.
                </p>
              </div>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSubmit)}
                  className="space-y-6"
                >
                  {/* Title */}
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input placeholder="Resource title" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Excerpt */}
                  <FormField
                    control={form.control}
                    name="excerpt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Excerpt</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Brief description or excerpt"
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Content - Rich Text Editor */}
                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Content</FormLabel>
                        <FormControl>
                          <div className="border border-input rounded-md">
                            <Editor
                              editorState={editorState}
                              onEditorStateChange={onEditorStateChange}
                              wrapperClassName="min-h-[300px]"
                              editorClassName="px-3 py-2 min-h-[250px]"
                              toolbarClassName="border-b border-input px-3 py-2"
                              toolbar={{
                                options: [
                                  'inline',
                                  'blockType',
                                  'fontSize',
                                  'list',
                                  'textAlign',
                                  'link',
                                  'history',
                                ],
                                inline: {
                                  options: ['bold', 'italic', 'underline'],
                                },
                                blockType: {
                                  options: [
                                    'Normal',
                                    'H1',
                                    'H2',
                                    'H3',
                                    'H4',
                                    'H5',
                                    'H6',
                                  ],
                                },
                                list: {
                                  options: ['unordered', 'ordered'],
                                },
                                textAlign: {
                                  options: ['left', 'center', 'right'],
                                },
                                link: {
                                  options: ['link'],
                                },
                              }}
                            />
                          </div>
                        </FormControl>
                        <p className="text-sm text-muted-foreground mt-1">
                          Use the rich text editor to format your content with
                          headings, lists, links, and more.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Category */}
                  <FormField
                    control={form.control}
                    name="category_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <FormControl>
                          <select
                            {...field}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <option value="">
                              Select a category (optional)
                            </option>
                            {categories.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Status and Featured in a row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <FormControl>
                            <select
                              {...field}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <option value={ResourceDocumentStatus.DRAFT}>
                                Draft
                              </option>
                              <option value={ResourceDocumentStatus.PUBLISHED}>
                                Published
                              </option>
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="featured"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-8">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Featured Resource</FormLabel>
                            <p className="text-sm text-muted-foreground">
                              Show this resource prominently on the platform
                            </p>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Color */}
                  <FormField
                    control={form.control}
                    name="color"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Theme Color</FormLabel>
                        <FormControl>
                          <div className="flex items-center space-x-3">
                            <input
                              type="color"
                              {...field}
                              className="h-10 w-20 rounded border border-input"
                            />
                            <Input
                              placeholder="#3b82f6"
                              {...field}
                              className="flex-1"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Form Actions */}
                  <div className="flex items-center justify-between pt-6">
                    <Link
                      to="/admin/resources"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium rounded-md transition-colors duration-200"
                    >
                      Cancel
                    </Link>

                    <Button
                      type="submit"
                      disabled={createLoading}
                      className="inline-flex items-center px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                    >
                      {createLoading && (
                        <Indicators.Spinner size="sm" className="mr-2" />
                      )}
                      Create Resource
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </div>
      </Layout.PaddedContent>
    )
  }

  // Render edit form when in edit mode
  if (isEditMode && resource) {
    return (
      <Layout.PaddedContent className="pb-24 md:pb-32">
        <Helmet>
          <title>Edit {resource.title} - MCSS Pro Connect</title>
        </Helmet>

        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <Navigation.ReturnLink to={`/admin/resources/show/${slug}`}>
              Back to Resource Details
            </Navigation.ReturnLink>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-8">
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Edit Resource
                </h1>
                <p className="text-gray-600">
                  Update the resource information below.
                </p>
              </div>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSubmit)}
                  className="space-y-6"
                >
                  {/* Title */}
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input placeholder="Resource title" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Excerpt */}
                  <FormField
                    control={form.control}
                    name="excerpt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Excerpt</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Brief description or excerpt"
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Content - Rich Text Editor */}
                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Content</FormLabel>
                        <FormControl>
                          <div className="border border-input rounded-md">
                            <Editor
                              editorState={editorState}
                              onEditorStateChange={onEditorStateChange}
                              wrapperClassName="min-h-[300px]"
                              editorClassName="px-3 py-2 min-h-[250px]"
                              toolbarClassName="border-b border-input px-3 py-2"
                              toolbar={{
                                options: [
                                  'inline',
                                  'blockType',
                                  'fontSize',
                                  'list',
                                  'textAlign',
                                  'link',
                                  'history',
                                ],
                                inline: {
                                  options: ['bold', 'italic', 'underline'],
                                },
                                blockType: {
                                  options: [
                                    'Normal',
                                    'H1',
                                    'H2',
                                    'H3',
                                    'H4',
                                    'H5',
                                    'H6',
                                  ],
                                },
                                list: {
                                  options: ['unordered', 'ordered'],
                                },
                                textAlign: {
                                  options: ['left', 'center', 'right'],
                                },
                                link: {
                                  options: ['link'],
                                },
                              }}
                            />
                          </div>
                        </FormControl>
                        <p className="text-sm text-muted-foreground mt-1">
                          Use the rich text editor to format your content with
                          headings, lists, links, and more.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Category */}
                  <FormField
                    control={form.control}
                    name="category_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <FormControl>
                          <select
                            {...field}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <option value="">
                              Select a category (optional)
                            </option>
                            {categories.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Status and Featured in a row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <FormControl>
                            <select
                              {...field}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <option value={ResourceDocumentStatus.DRAFT}>
                                Draft
                              </option>
                              <option value={ResourceDocumentStatus.PUBLISHED}>
                                Published
                              </option>
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="featured"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-8">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Featured Resource</FormLabel>
                            <p className="text-sm text-muted-foreground">
                              Show this resource prominently on the platform
                            </p>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Color */}
                  <FormField
                    control={form.control}
                    name="color"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Theme Color</FormLabel>
                        <FormControl>
                          <div className="flex items-center space-x-3">
                            <input
                              type="color"
                              {...field}
                              className="h-10 w-20 rounded border border-input"
                            />
                            <Input
                              placeholder="#3b82f6"
                              {...field}
                              className="flex-1"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Form Actions */}
                  <div className="flex items-center justify-between pt-6">
                    <Link
                      to={`/admin/resources/show/${slug}`}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium rounded-md transition-colors duration-200"
                    >
                      Cancel
                    </Link>

                    <Button
                      type="submit"
                      disabled={updateLoading}
                      className="inline-flex items-center px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                    >
                      {updateLoading && (
                        <Indicators.Spinner size="sm" className="mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </form>
              </Form>

              {/* Delete Section for existing resources */}
              {resource && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <div className="bg-red-50 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-red-900 mb-2">
                      Danger Zone
                    </h3>
                    <p className="text-sm text-red-700 mb-4">
                      Permanently delete this resource. This action cannot be
                      undone.
                    </p>
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={deleteLoading}
                      onClick={() => set({ modal: 'destroy' })}
                    >
                      {deleteLoading && (
                        <Indicators.Spinner size="sm" className="mr-2" />
                      )}
                      Delete Resource
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Delete Confirmation Modal */}
          {search.modal === 'destroy' && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3 text-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    Delete Resource?
                  </h3>
                  <div className="mt-2 px-7 py-3">
                    <p className="text-sm text-gray-500">
                      {modalError ||
                        "Are you sure you'd like to delete this resource? This cannot be undone."}
                    </p>
                  </div>
                  <div className="flex justify-center space-x-4 px-4 py-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        set({ modal: '' })
                        setModalError(null)
                      }}
                      disabled={deleteLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleDestroy}
                      disabled={deleteLoading}
                    >
                      {deleteLoading && (
                        <Indicators.Spinner size="sm" className="mr-2" />
                      )}
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Layout.PaddedContent>
    )
  }

  // Render read-only view
  return (
    <Layout.PaddedContent className="pb-24 md:pb-32">
      <Helmet>
        <title>{resource?.title || 'Resource'} - MCSS Pro Connect</title>
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Navigation.ReturnLink to="/admin/resources">
            Return to Resources
          </Navigation.ReturnLink>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-8">
            <div className="flex items-start space-x-6 mb-8">
              <Avatar.Simple
                name={resource?.title || 'Resource'}
                maxInitials={1}
                color={resource?.color || '#333'}
                size={72}
                imageUrl={resource?.icon_url || ''}
              />
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {resource?.title}
                </h1>
                {resource?.category?.name && (
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-4">
                    {resource.category.name}
                  </div>
                )}
                {resource?.excerpt && (
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {resource.excerpt}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-50 p-4 rounded-lg">
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      resource?.status === 'PUBLISHED'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {resource?.status}
                  </span>
                </dd>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <dt className="text-sm font-medium text-gray-500">Featured</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {resource?.featured ? 'Yes' : 'No'}
                </dd>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <dt className="text-sm font-medium text-gray-500">Published</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {resource?.published_at
                    ? new Date(resource.published_at).toLocaleDateString()
                    : 'Not published'}
                </dd>
              </div>
            </div>

            {resource?.content &&
              (() => {
                // Try to render rich text content
                try {
                  const contentState = convertFromRaw(
                    JSON.parse(resource.content)
                  )
                  const editorStateForDisplay = EditorState.createWithContent(
                    contentState
                  )

                  return (
                    <div className="prose max-w-none">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Content
                      </h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <Editor
                          editorState={editorStateForDisplay}
                          toolbarHidden={true}
                          readOnly={true}
                          wrapperClassName="demo-wrapper"
                          editorClassName="demo-editor text-gray-700"
                        />
                      </div>
                    </div>
                  )
                } catch (error) {
                  // Fallback to plain text if content is not in Draft.js format
                  return (
                    <div className="prose max-w-none">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Content
                      </h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-700 whitespace-pre-wrap">
                          {resource.content}
                        </p>
                      </div>
                    </div>
                  )
                }
              })()}
          </div>
        </div>

        <div className="mt-6 flex justify-between">
          <Link
            to="/admin/resources"
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Back to List
          </Link>

          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-500">
              Last updated:{' '}
              {resource?.updated_at
                ? new Date(resource.updated_at).toLocaleDateString()
                : 'Unknown'}
            </div>
            {!isEditMode && !isNewMode && (
              <Link
                to={`/admin/resources/edit/${slug}`}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
              >
                Edit Resource
              </Link>
            )}
          </div>
        </div>
      </div>
    </Layout.PaddedContent>
  )
}
